import { useEffect, useRef, useState } from 'react';
import ConsentCheckbox from './ConsentCheckbox';
import { FormControl, FormLabel, Input, Button, Alert, Typography, Stack } from '@mui/joy';
import { apiRequestCode, apiVerifyCode } from '../../auth';
import { formatPhoneRu, phoneDigitsCount } from '../../utils';

// Длина кода по каналу (совпадает с бэкендом: email/sms — 6, звонок — capacity, по умолчанию 4)
const CODE_LEN = { email: 6, sms: 6, call: 4 };

/**
 * Универсальная форма входа по коду.
 * props:
 *  - role: 'doctor' | 'patient' — кабинет (для поиска профиля на бэке)
 *  - mode: 'email' | 'phone'    — тип контакта
 *  - onSuccess: (profile) => void — вызывается после успешного входа
 */
export default function CodeLoginForm({ role, consentAccepted = false, onConsentChange, mode, onSuccess }) {
  const [step, setStep] = useState('request');   // 'request' | 'verify'
  const [contact, setContact] = useState('');      // email или телефон (форматированный)
  const [channel, setChannel] = useState(mode === 'email' ? 'email' : 'call');
  const [masked, setMasked] = useState('');
  const [code, setCode] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => {
    if (consentAccepted) setError((previous) => previous === 'Подтвердите согласие на обработку персональных данных' ? '' : previous);
  }, [consentAccepted]);

  // Обратный отсчёт до разблокировки «Отправить снова»
  useEffect(() => {
    if (secondsLeft <= 0) return;
    timerRef.current = setInterval(() => setSecondsLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(timerRef.current);
  }, [secondsLeft]);

  const destinationValue = () =>
    mode === 'email' ? contact.trim().toLowerCase() : contact.trim();

  const validContact = () =>
    mode === 'email'
      ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.trim())
      : phoneDigitsCount(contact) === 11;

  const requestCode = async (ch) => {
    setError('');
    if (loading) return;
    if (!consentAccepted) { setError('Подтвердите согласие на обработку персональных данных'); return; }
    if (!validContact()) {
      setError(mode === 'email' ? 'Введите корректный email' : 'Введите телефон полностью');
      return;
    }
    setChannel(ch);
    setLoading(true);
    try {
      const resp = await apiRequestCode({ consentAccepted, role, channel: ch, destination: destinationValue() });
      if (!resp?.success) {
        setError(resp?.error || 'Не удалось отправить код');
        return;
      }
      setMasked(resp.destination_masked || '');
      setSecondsLeft(60);        // антифлуд: повтор через минуту
      setCode('');
      setStep('verify');
    } catch (e) {
      if (e?.response?.status === 429) setError('Код уже отправлен. Повторите через минуту.');
      else setError(e?.response?.data?.error || 'Не удалось отправить код');
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setError('');
    if (loading) return;
    if (!consentAccepted) { setError('Подтвердите согласие на обработку персональных данных'); return; }
    const need = CODE_LEN[channel] || 6;
    if (code.replace(/\D/g, '').length < need) {
      setError(`Введите код из ${need} цифр`);
      return;
    }
    setLoading(true);
    try {
      const resp = await apiVerifyCode({ consentAccepted,
        role,
        channel,
        destination: destinationValue(),
        code: code.replace(/\D/g, ''),
        device_name: navigator.userAgent,
      });
      if (!resp?.success) {
        setError(resp?.error || 'Неверный код');
        return;
      }
      onSuccess?.(resp.profile);
    } catch (e) {
      setError(e?.response?.data?.error || 'Неверный код');
    } finally {
      setLoading(false);
    }
  };

  // ── Шаг 1: ввод контакта и запрос кода ──
  if (step === 'request') {
    return (
      <>
        {error && <Alert color="danger" variant="soft">{error}</Alert>}

        {mode === 'email' ? (
          <FormControl>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              placeholder="you@mail.ru"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && requestCode('email')}
            />
          </FormControl>
        ) : (
          <FormControl>
            <FormLabel>Телефон</FormLabel>
            <Input
              type="tel"
              placeholder="+7 (999) 123-45-67"
              value={contact}
              onChange={(e) => setContact(formatPhoneRu(e.target.value, contact))}
              slotProps={{ input: { inputMode: 'tel', maxLength: 18 } }}
            />
          </FormControl>
        )}

        {mode === 'email' ? (
          <Button loading={loading} disabled={!consentAccepted} onClick={() => requestCode('email')} sx={{ mt: 0.5 }}>
            Получить код на почту
          </Button>
        ) : (
          <Button loading={loading} disabled={!consentAccepted} onClick={() => requestCode('call')} sx={{ mt: 0.5 }}>
            Получить код звонком
          </Button>
        )}
        <ConsentCheckbox checked={consentAccepted} onChange={onConsentChange} disabled={loading} />

        <Typography level="body-xs" textColor="text.tertiary" sx={{ textAlign: 'center' }}>
          {mode === 'email'
            ? 'Пришлём одноразовый код на вашу почту'
            : 'Вам поступит звонок — введёте последние цифры номера'}
        </Typography>
      </>
    );
  }

  // ── Шаг 2: ввод кода ──
  const need = CODE_LEN[channel] || 6;
  return (
    <>
      {error && <Alert color="danger" variant="soft">{error}</Alert>}

      <Typography level="body-sm" sx={{ textAlign: 'center' }} textColor="text.tertiary">
        {channel === 'call'
          ? `Вам поступит звонок — введите последние ${need} цифр номера`
          : <>Код отправлен на <b>{masked}</b></>}
      </Typography>

      <FormControl>
        <FormLabel>Код из {need} цифр</FormLabel>
        <Input
          autoFocus
          placeholder={'•'.repeat(need)}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, need))}
          onKeyDown={(e) => e.key === 'Enter' && verify()}
          slotProps={{ input: { inputMode: 'numeric', maxLength: need } }}
        />
      </FormControl>

      <Button loading={loading} disabled={!consentAccepted} onClick={verify} sx={{ mt: 0.5 }}>
        Войти
      </Button>
      <ConsentCheckbox checked={consentAccepted} onChange={onConsentChange} disabled={loading} />

      <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
        <Button
          variant="plain" color="neutral" size="sm"
          onClick={() => { setStep('request'); setError(''); }}
        >
          Изменить {mode === 'email' ? 'почту' : 'телефон'}
        </Button>
        <Button
          variant="plain" size="sm"
          disabled={!consentAccepted || loading || secondsLeft > 0}
          onClick={() => requestCode(channel)}
        >
          {secondsLeft > 0 ? `Отправить снова (${secondsLeft})` : 'Отправить снова'}
        </Button>
      </Stack>
    </>
  );
}